<template>
    <div class="template-editor">
        <TemplateToolbar
            v-model="activeBlock"
            :blockIndex="activeBlockIndex"
            :fonts="fonts"
            :canUndo="canUndo"
            :canRedo="canRedo"
            v-model:previewWithData="previewWithData"
            v-model:previewRowKey="previewRowKey"
            :previewOptions="previewOptions"
            @addText="addBlock"
            @addImage="addImageBlock"
            @uploadBackground="uploadBackgroundImage"
            @undo="undo"
            @redo="redo"
            @beforeBlockChange="onBeforeToolbarBlockChange"
        />

        <div class="canvas-area">
            <TemplatePreview
                v-if="project_id > 0"
                v-model="template"
                :project="props.project"
                @blockClick="activateBlock"
                :activeBlock="activeBlockId"
                @moveBlock="onMoveBlock"
                @onTextWidgetDblClick="enableEditMode"
                @blockInteractionStart="onBlockInteractionStart"
                @blockInteractionEnd="onBlockInteractionEnd"
                @textEditCommitted="onTextEditCommitted"
                :editMode="editMode"
                :wildcards="wildcardList"
                :dataSet="previewDataSet"
            />
        </div>
    </div>

    <BackgroundCropDialog
        v-model="showCropDialog"
        :imageSrc="cropImageSrc"
        :imageWidth="cropImageWidth"
        :imageHeight="cropImageHeight"
        :orientation="template.orientation"
        :filename="cropFilename"
        @apply="onCropApply"
        @cancel="onCropCancel"
    />
    <ConfirmDialog />
</template>

<script setup>
import api from "@/libs/api";
import { consumeTemplateUnmountSaveSkip } from "@/libs/templateSaveGuard";
import TemplatePreview from "@/components/TemplatePreview";
import TemplateToolbar from "@/components/TemplateToolbar";
import BackgroundCropDialog from "@/components/BackgroundCropDialog";

import {ref, computed, defineProps, watch, onUnmounted, onMounted, onBeforeUnmount} from "vue"
import {useRoute} from "vue-router"
import { useConfirm } from "primevue/useconfirm";

import {round, pixelToMM} from "@/libs/utils"
import { buildDataSet, buildPreviewOptions } from "@/libs/wildcards"

const confirm = useConfirm();
const props = defineProps(['project', 'dataVersion']);

const project_id = ref(-1);
const activeBlockId = ref(-1);

const fonts = ref([]);
const dataKeys = ref([]);
const dataRows = ref([]);

const previewWithData = ref(false);
const previewRowKey = ref(null);

const previewOptions = computed(() => buildPreviewOptions(dataRows.value, 4));

const previewDataSet = computed(() => {
    if (!previewWithData.value) return null;
    const key = previewRowKey.value ?? previewOptions.value[0]?.value;
    return buildDataSet(dataKeys.value, dataRows.value, key);
});

const systemWildcards = [
    { value: 'row', label: 'Номер строки' },
    { value: 'system.date', label: 'Текущая дата' },
    { value: 'system.time', label: 'Текущее время' },
    { value: 'system.datetime', label: 'Дата и время' },
];

const wildcardList = computed(() => {
    const fromData = dataKeys.value.map(k => ({
        value: k.name,
        label: ''
    }));
    return [...fromData, ...systemWildcards];
});

const template = ref({blocks:[]});
const HISTORY_LIMIT = 50;
const undoStack = ref([]);
const redoStack = ref([]);
const interactionStartSnapshot = ref(null);
const textEditStartSnapshot = ref(null);
const toolbarChangeKey = ref(null);
let toolbarChangeTimer = null;

const canUndo = computed(() => undoStack.value.length > 0);
const canRedo = computed(() => redoStack.value.length > 0);

const cloneBlocks = (blocks = template.value.blocks) => JSON.parse(JSON.stringify(blocks || []));
const serializeBlocks = (blocks) => JSON.stringify(blocks || []);
const normalizeHistorySnapshot = (snapshot) => {
    if (Array.isArray(snapshot)) {
        return {
            blocks: cloneBlocks(snapshot),
            activeBlockId: activeBlockId.value,
        };
    }

    return {
        blocks: cloneBlocks(snapshot?.blocks),
        activeBlockId: snapshot?.activeBlockId ?? -1,
    };
};
const createHistorySnapshot = () => ({
    blocks: cloneBlocks(),
    activeBlockId: activeBlockId.value,
});
const blocksChanged = (before, after = template.value.blocks) => {
    const blocks = Array.isArray(before) ? before : before?.blocks;
    return serializeBlocks(blocks) !== serializeBlocks(after);
};
const clearToolbarChangeGroup = () => {
    toolbarChangeKey.value = null;
    if (toolbarChangeTimer) {
        clearTimeout(toolbarChangeTimer);
        toolbarChangeTimer = null;
    }
};

const pushHistorySnapshot = (stack, snapshot) => {
    const normalized = normalizeHistorySnapshot(snapshot);
    stack.value.push(normalized);
    if (stack.value.length > HISTORY_LIMIT) stack.value.shift();
};

const pushUndoSnapshot = (snapshot, { clearRedo = true } = {}) => {
    clearToolbarChangeGroup();
    if (!blocksChanged(snapshot)) return;
    const top = undoStack.value[undoStack.value.length - 1];
    const normalized = normalizeHistorySnapshot(snapshot);
    if (top && serializeBlocks(top.blocks) === serializeBlocks(normalized.blocks)) return;
    if (clearRedo) redoStack.value = [];
    pushHistorySnapshot(undoStack, normalized);
};

const recordHistoryBeforeChange = () => createHistorySnapshot();

const applyBlocksSnapshot = (snapshot) => {
    const normalized = normalizeHistorySnapshot(snapshot);
    template.value.blocks = cloneBlocks(normalized.blocks);

    const restoredActiveBlockId = normalized.activeBlockId;
    if (restoredActiveBlockId !== -1 && template.value.blocks.some(block => block.id === restoredActiveBlockId)) {
        activeBlockId.value = restoredActiveBlockId;
    } else {
        activeBlockId.value = -1;
        disableEditMode();
    }
};

const undo = () => {
    if (!canUndo.value) return;
    clearToolbarChangeGroup();
    pushHistorySnapshot(redoStack, createHistorySnapshot());
    applyBlocksSnapshot(undoStack.value.pop());
};

const redo = () => {
    if (!canRedo.value) return;
    clearToolbarChangeGroup();
    pushHistorySnapshot(undoStack, createHistorySnapshot());
    applyBlocksSnapshot(redoStack.value.pop());
};

const resetHistory = () => {
    undoStack.value = [];
    redoStack.value = [];
    interactionStartSnapshot.value = null;
    textEditStartSnapshot.value = null;
    clearToolbarChangeGroup();
};

const onBlockInteractionStart = () => {
    interactionStartSnapshot.value = recordHistoryBeforeChange();
};

const onBlockInteractionEnd = () => {
    if (!interactionStartSnapshot.value) return;
    pushUndoSnapshot(interactionStartSnapshot.value);
    interactionStartSnapshot.value = null;
};

const onTextEditCommitted = () => {
    disableEditMode();
    if (!textEditStartSnapshot.value) return;
    pushUndoSnapshot(textEditStartSnapshot.value);
    textEditStartSnapshot.value = null;
};

const onBeforeToolbarBlockChange = (changeKey = 'toolbar') => {
    if (toolbarChangeKey.value !== changeKey) {
        pushHistorySnapshot(undoStack, createHistorySnapshot());
        redoStack.value = [];
    }
    toolbarChangeKey.value = changeKey;
    if (toolbarChangeTimer) clearTimeout(toolbarChangeTimer);
    toolbarChangeTimer = setTimeout(() => {
        toolbarChangeKey.value = null;
        toolbarChangeTimer = null;
    }, 500);
};

const activeBlock = computed({
    get: () =>
        activeBlockId.value !== -1
            ? template.value.blocks.find(block => block.id === activeBlockId.value) || null
            : null,
    set: (value) => {
        // TemplateToolbar uses v-model; this setter keeps Vue quiet while block fields mutate by reference.
        if (!value || activeBlockId.value === -1) return;
        const idx = template.value.blocks.findIndex(block => block.id === activeBlockId.value);
        if (idx !== -1) template.value.blocks[idx] = value;
    }
});

const activeBlockIndex = computed(() =>
    activeBlockId.value !== -1
        ? template.value.blocks.findIndex(block => block.id === activeBlockId.value)
        : -1
);

// clipboard for copied text block
const copiedBlock = ref(null);

const copyActiveBlock = () => {
    if (!activeBlock.value) return;
    if (activeBlock.value.type !== 'text') return;
    const { ...rest } = JSON.parse(JSON.stringify(activeBlock.value));
    copiedBlock.value = {
        ...rest,
        source: { x: activeBlock.value.x, y: activeBlock.value.y, height: activeBlock.value.height }
    };
};

const pasteCopiedBlock = () => {
    if (!copiedBlock.value) return;
    const before = recordHistoryBeforeChange();
    const margin = 2;
    const srcX = copiedBlock.value.source.x;
    const srcY = copiedBlock.value.source.y;
    const srcH = copiedBlock.value.source.height;

    const nearTop = srcY <= margin;
    const nearBottom = srcY + srcH + margin >= template.value.height;

    let newY = srcY + srcH + margin;
    if (nearBottom) newY = Math.max(0, srcY - srcH - margin);
    if (nearTop) newY = srcY + srcH + margin;

    const clampedY = Math.min(Math.max(0, newY), template.value.height - copiedBlock.value.height);

    const newBlock = {
        id: makeId(8),
        type: 'text',
        x: srcX,
        y: clampedY,
        width: copiedBlock.value.width,
        height: copiedBlock.value.height,
        border: copiedBlock.value.border,
        border_width: copiedBlock.value.border_width,
        border_color: copiedBlock.value.border_color,
        background: copiedBlock.value.background ?? "",
        text: copiedBlock.value.text,
        align: copiedBlock.value.align,
        font: JSON.parse(JSON.stringify(copiedBlock.value.font))
    };
    template.value.blocks.push(newBlock);
    activeBlockId.value = newBlock.id;
    pushUndoSnapshot(before);
};

const onMoveBlock = (event) => {
    const block = template.value.blocks.find(block => block.id === event.blockId);
    if (!block) return;
    const before = recordHistoryBeforeChange();
    block.x += event.x;
    block.y += event.y;
    pushUndoSnapshot(before);
}

const deleteBlock = (blockId) => {
    const before = recordHistoryBeforeChange();
    template.value.blocks = template.value.blocks.filter(block => block.id !== blockId);
    if (activeBlockId.value === blockId) {
        activeBlockId.value = -1;
    }
    pushUndoSnapshot(before);
};

const deleteActiveBlock = () => {
    if (!activeBlock.value) return;
    confirm.require({
        message: 'Вы действительно хотите удалить этот блок?',
        header: 'Подтвердите удаление',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
            deleteBlock(activeBlock.value.id);
        },
        reject: () => {}
    });
};

const itemToSave = computed(() => {
    let result = Object.assign({}, template.value);
    result.blocks = JSON.stringify(result.blocks.map(block => {
        block.border = block.border_width > 0 ? 1 : 0;
        return block;
    }));
    return result;
});

// Слепок последнего загруженного/сохранённого состояния: autosave на unmount
// выполняем только при наличии локальных правок. Иначе сохранение не нужно,
// а для устаревшего экземпляра (после действий ассистента) — ещё и вредно:
// оно затирает свежие изменения ассистента в БД.
const lastSavedJson = ref(null);
const serializeForSave = () => JSON.stringify(itemToSave.value);

const save = () => {
    // Ремаунт после действий ассистента: уходящий экземпляр устарел, его autosave
    // затрёт свежие изменения ассистента в БД — пропускаем.
    if (consumeTemplateUnmountSaveSkip()) return Promise.resolve();
    // Шаблон ещё не загружался — сохранять нечего.
    if (lastSavedJson.value === null) return Promise.resolve();
    // Локальных правок не было — сохранять нечего.
    const current = serializeForSave();
    if (current === lastSavedJson.value) return Promise.resolve();

    lastSavedJson.value = current;
    return api.updateProjectTemplate(project_id.value, itemToSave.value);
};
onUnmounted(() => save());

const makeId = (length) => {
    let result = '';
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

const addBlock = () => {
    const before = recordHistoryBeforeChange();
    const newBlock = {
        id: makeId(8),
        x: 0,
        y: 0,
        width: 200,
        height: 10,
        border: 0,
        border_width: 0,
        border_color: '#000000',
        background: "",
        text: 'введите текст',
        type: 'text',
        align: 'C',
        font: {
            color: '#000',
            size: 16,
            style: '',
            family: 'Arial'
        }
    };
    template.value.blocks.push(newBlock);
    activeBlockId.value = newBlock.id;
    pushUndoSnapshot(before);
}

const load = () => {
    api.viewProjectTemplate(project_id.value)
        .then(data => {
            data.blocks = JSON.parse(data.blocks).map(block => {
                if (block.background === undefined) block.background = "";
                if (block.border_width === undefined) {
                    block.border_width = 0;
                } else {
                    block.border_width = parseInt(block.border_width);
                }
                if (block.border_color === undefined) block.border_color = "#000000";
                return block;
            });
            template.value = data;
            resetHistory();
            lastSavedJson.value = serializeForSave();
        })
}

// Ключи и строки данных для предпросмотра «С данными». Вызывается при монтировании
// и когда ассистент изменил данные проекта (родитель инкрементирует dataVersion).
const loadData = () => {
    api.viewProjectData(project_id.value).then(data => {
        dataKeys.value = data.keys || [];
        dataRows.value = data.values || [];
        if (!previewRowKey.value && previewOptions.value.length) {
            previewRowKey.value = previewOptions.value[0].value;
        }
    }).catch(() => {});
};

watch(() => props.dataVersion, loadData);

const BG_DIMENSIONS = {
    L: { width: 1126, height: 796 },
    P: { width: 796, height: 1126 },
};

const showCropDialog = ref(false);
const cropImageSrc = ref('');
const cropImageWidth = ref(0);
const cropImageHeight = ref(0);
const cropFilename = ref('');

const uploadBackgroundImage = (data) => {
    api.uploadProjectTemplateImage(project_id.value, data.files).then((uploadResult) => {
        const dims = BG_DIMENSIONS[template.value.orientation];

        if (uploadResult.width < dims.width || uploadResult.height < dims.height) {
            window.alert(
                `Изображение слишком маленькое (${uploadResult.width} x ${uploadResult.height}). ` +
                `Минимальный требуемый размер: ${dims.width} x ${dims.height} пикселей.`
            );
            return;
        }

        const ratioImg = uploadResult.width / uploadResult.height;
        const ratioReq = dims.width / dims.height;

        if (Math.abs(ratioImg - ratioReq) < 0.01) {
            api.processBackground(project_id.value, {
                filename: uploadResult.filename,
                orientation: template.value.orientation,
                crop_x: 0,
                crop_y: 0,
                crop_width: uploadResult.width,
                crop_height: uploadResult.height,
            }).then(result => template.value.background_file = result.filename);
        } else {
            cropFilename.value = uploadResult.filename;
            cropImageWidth.value = uploadResult.width;
            cropImageHeight.value = uploadResult.height;
            cropImageSrc.value = `${api.server}/projects/${props.project.guid}/images/${uploadResult.filename}`;
            showCropDialog.value = true;
        }
    });
}

const onCropApply = (cropData) => {
    api.processBackground(project_id.value, cropData).then(
        result => template.value.background_file = result.filename
    );
}

const onCropCancel = () => {
    showCropDialog.value = false;
}

const addImageBlock = (data) => {
    api.uploadProjectTemplateImage(project_id.value, data.files).then(
        (data) => {
            const before = recordHistoryBeforeChange();
            const imgBlock = {
                id: makeId(8),
                x: 0,
                y: 0,
                width: 200,
                height: 10,
                border: 0,
                border_width: 0,
                border_color: '#000000',
                type: 'img',
                src: data.filename,
                ext: data.ext,
                orig_width: pixelToMM(data.width),
                orig_height: pixelToMM(data.height),
                keepProportional: 1,
                rate: round(data.width / data.height, 2)
            }

            let maxWidth = template.value.width;
            let maxheight = template.value.height;

            if (imgBlock.orig_width > imgBlock.orig_height) {
                imgBlock.width = imgBlock.orig_width > maxWidth ? maxWidth : imgBlock.orig_width;
                imgBlock.height = imgBlock.width / imgBlock.rate;
            } else {
                imgBlock.height = imgBlock.orig_height > maxheight ? maxheight : imgBlock.orig_height;
                imgBlock.width = imgBlock.rate * imgBlock.height;
            }
            template.value.blocks.push(imgBlock)
            activeBlockId.value = imgBlock.id;
            pushUndoSnapshot(before);
        });
}

const moveUp = (pixels) => {
    if (activeBlock.value.y - pixels > 0) activeBlock.value.y -= pixels;
    else activeBlock.value.y = 0
}

const moveDown = (pixels) => {
    if (activeBlock.value.y + activeBlock.value.height + pixels < template.value.height) activeBlock.value.y += pixels;
    else activeBlock.value.y = template.value.height - activeBlock.value.height;
}

const moveLeft = (pixels) => {
    if (activeBlock.value.x - pixels > 0) activeBlock.value.x -= pixels;
    else activeBlock.value.x = 0
}

const moveRight = (pixels) => {
    if (activeBlock.value.x + activeBlock.value.width + pixels < template.value.width)
        activeBlock.value.x += pixels;
    else activeBlock.value.x = template.value.width - activeBlock.value.width;
}

const increaseWidth = (pixels) => activeBlock.value.width += pixels;
const decreaseWidth = (pixels) => activeBlock.value.width - pixels > 30 ? activeBlock.value.width -= pixels : 0;

const increaseHeight = (pixels) => {
    const pixelsInMm = 3.79;
    let fontSizeInMM = activeBlock.value.font.size / pixelsInMm;
    if (activeBlock.value.height - pixels > fontSizeInMM) activeBlock.value.height -= pixels;
    else activeBlock.value.height = fontSizeInMM;
}
const decreaseHeight = (pixels) => activeBlock.value.height += pixels;

api.getAvailableFonts().then(data => fonts.value = data);

const editMode = ref(false);
const enableEditMode = () => {
    if (textEditStartSnapshot.value) return;
    textEditStartSnapshot.value = recordHistoryBeforeChange();
    editMode.value = true;
};
const disableEditMode = () => { editMode.value = false };
const activateBlock = (blockId) => {
    activeBlockId.value = blockId;
    disableEditMode();
}

const onKeyDown = (event) => {
    const isEditableTarget = event.target && (
        event.target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)
    );
    const isUndo = event.ctrlKey && !event.shiftKey && event.code === 'KeyZ';
    const isRedo = (event.ctrlKey && event.code === 'KeyY') || (event.ctrlKey && event.shiftKey && event.code === 'KeyZ');

    if (!isEditableTarget && (isUndo || isRedo)) {
        event.preventDefault();
        event.stopPropagation();
        isUndo ? undo() : redo();
        return;
    }

    if (activeBlock.value !== null && !editMode.value) {
        // Delete key — remove active block
        if (event.code === 'Delete') {
            event.preventDefault();
            event.stopPropagation();
            deleteActiveBlock();
            return;
        }

        // Copy / paste
        if (event.ctrlKey && event.code === 'KeyC') {
            event.preventDefault();
            event.stopPropagation();
            copyActiveBlock();
            return;
        }
        if (event.ctrlKey && event.code === 'KeyV') {
            event.preventDefault();
            event.stopPropagation();
            pasteCopiedBlock();
            return;
        }

        const isArrowKey = ['ArrowRight', 'ArrowDown', 'ArrowUp', 'ArrowLeft'].includes(event.code);
        if (!isArrowKey) return;

        event.preventDefault();
        event.stopPropagation();
        const before = recordHistoryBeforeChange();

        let moveHandler = {
            ArrowUp: moveUp,
            ArrowDown: moveDown,
            ArrowLeft: moveLeft,
            ArrowRight: moveRight,
        }
        let resizeHandler = {
            ArrowUp: increaseHeight,
            ArrowDown: decreaseHeight,
            ArrowLeft: decreaseWidth,
            ArrowRight: increaseWidth,
        }
        let step = event.shiftKey ? 10 : 1;
        let handler = event.ctrlKey ? resizeHandler : moveHandler;
        handler[event.code](step);
        pushUndoSnapshot(before);
    }
};

onMounted(() => {
    document.addEventListener('keydown', onKeyDown);
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeyDown);
    clearToolbarChangeGroup();
});

project_id.value = useRoute().params.project_id;
load();
loadData();
</script>

<style scoped>
.template-editor {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 194px);
    min-height: 0;
}
.canvas-area {
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
}
</style>
